import React from 'react';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

export interface ExamPhoneNavProps {
    answeredCount: number;
    totalQuestions: number;
    prevDisabled: boolean;
    nextDisabled: boolean;
    onPrev: () => void;
    onNext: () => void;
    onOpenPalette: () => void;
    paletteOpen: boolean;
    className?: string;
}

/** Phone bottom bar: labelled previous and next around an icon button that opens the question palette. Sticks to the bottom edge. */
export function ExamPhoneNav({
    answeredCount,
    totalQuestions,
    prevDisabled,
    nextDisabled,
    onPrev,
    onNext,
    onOpenPalette,
    paletteOpen,
    className,
}: ExamPhoneNavProps) {
    const { t } = useTranslation();
    return (
        <nav
            aria-label={t('question_navigator')}
            className={cn(
                'sticky bottom-0 z-20 mt-auto flex min-h-[88px] shrink-0 items-center border-t border-border bg-card px-4 pb-[max(24px,env(safe-area-inset-bottom))] pt-3',
                className,
            )}
        >
            {/* On tablets the controls line up with the 720px question column. */}
            <div className="mx-auto flex w-full max-w-[688px] items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    className="h-12 min-w-0 flex-1 gap-1.5 px-3"
                    onClick={onPrev}
                    disabled={prevDisabled}
                >
                    <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                    {t('exam.nav.previous')}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-12 w-12 shrink-0"
                    title={`${t('questions')} ${answeredCount}/${totalQuestions}`}
                    aria-label={`${t('questions')} ${answeredCount}/${totalQuestions}`}
                    aria-haspopup="dialog"
                    aria-expanded={paletteOpen}
                    onClick={onOpenPalette}
                >
                    <LayoutGrid className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                    type="button"
                    variant="ink"
                    className="h-12 min-w-0 flex-1 gap-1.5 px-3"
                    onClick={onNext}
                    disabled={nextDisabled}
                >
                    {t('next')}
                    <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                </Button>
            </div>
        </nav>
    );
}

export default ExamPhoneNav;
